import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchStoreModules,
  MODULES_FAIL_OPEN,
  type StoreModules,
} from '../services/storeModulesApi';

const POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 min → la borne se débloque seule

interface UseStoreModulesOptions {
  /** Désactive le polling (ex: mode admin) */
  enabled?: boolean;
  /** Attendre d'avoir le store_id avant d'appeler */
  ready?: boolean;
}

interface UseStoreModulesResult {
  modules: StoreModules;
  /** true seulement si le backend a explicitement répondu is_kiosk_enabled=false */
  isKioskDisabled: boolean;
  /** Message du backend si une commande a été refusée (module_disabled) */
  disabledMessage: string;
  refresh: () => Promise<void>;
  /** Applique un refus reçu de create_order */
  applyModuleDisabled: (description: string) => void;
}

/**
 * v5 — Hook des modules store.
 *
 * - Appelle get_store_modules au démarrage (dès que le store_id est dispo)
 * - Re-vérifie toutes les 3 min → si l'admin réactive, la borne se débloque
 *   toute seule, sans redémarrage.
 * - FAIL-OPEN : erreur réseau → on considère tout activé, on ne bloque pas.
 */
export function useStoreModules(
  options: UseStoreModulesOptions = {}
): UseStoreModulesResult {
  const { enabled = true, ready = true } = options;

  const [modules, setModules] = useState<StoreModules>(MODULES_FAIL_OPEN);
  const [disabledMessage, setDisabledMessage] = useState('');
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!enabled || !ready) return;
    const result = await fetchStoreModules();
    if (!mountedRef.current) return;
    if (!result) {
      // FAIL-OPEN : on ne bloque jamais sur une erreur technique
      console.warn('[Modules] Échec de récupération → fail-open (kiosk actif)');
      setModules(MODULES_FAIL_OPEN);
      return;
    }
    setModules(result);
    // Si le kiosk est réactivé, on efface le message de refus
    if (result.is_kiosk_enabled) setDisabledMessage('');
  }, [enabled, ready]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled || !ready) return;

    refresh();
    const intervalId = setInterval(refresh, POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [enabled, ready, refresh]);

  const applyModuleDisabled = useCallback((description: string) => {
    console.warn('[Modules] Commande refusée: module_disabled', description);
    setDisabledMessage(description);
    setModules((prev) => ({ ...prev, is_kiosk_enabled: false }));
  }, []);

  // On ne bloque QUE si le backend a explicitement répondu false.
  // MODULES_FAIL_OPEN a is_kiosk_enabled=true → jamais de blocage par défaut.
  const isKioskDisabled = enabled && modules.is_kiosk_enabled === false;

  return {
    modules,
    isKioskDisabled,
    disabledMessage,
    refresh,
    applyModuleDisabled,
  };
}
